import dayjs from 'dayjs';
import React from 'react';

import BadgeNice from '@/core/components/badges/BadgeNice';
import BadgePunctual from '@/core/components/badges/BadgePunctual';
import Icons from '@/core/components/Icons';
import Image from '@/core/components/Image';
import type UserDomain from '@/core/lib/new-architecture/domain/user.domain';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

dayjs.locale('fr');

interface ProfileInfoProps {
  user: UserDomain;
  direction?: 'start';
  link?: boolean;
  hasH1?: boolean;
}

const ProfileInfo: React.FC<ProfileInfoProps> = ({ user, direction, link, hasH1 = false }) => {
  const { t } = useTranslationContext(['common']);
  const { Link } = useRouterContext();

  const { avatarUrl, username, city, createdAt, isNice, isPunctual } = user.getPublicInfo();
  const UsernameTag = hasH1 ? 'h1' : 'p';

  const info = (
    <section className={`flex flex-row items-start gap-3 lg:items-center ${direction === 'start' ? '' : 'lg:justify-center'} lg:gap-6`}>
      <Image
        loading="lazy"
        decoding="async"
        className="size-12 rounded-full lg:size-25"
        src={avatarUrl.replace('{{size}}', '3').replace('{{ext}}', 'webp')}
        type="image/webp"
        fallback={avatarUrl.replace('{{size}}', '3').replace('{{ext}}', 'jpg')}
        fallbackType="image/jpeg"
        alt={username}
      />

      <ul className="flex flex-col gap-1">
        <li className="hidden items-center gap-1 lg:inline-flex">
          <UsernameTag className="text-body-primary font-semibold text-content-primary">{username}</UsernameTag>
        </li>

        {city && (
          <li className="inline-flex items-center gap-1">
            <div className="hidden lg:block">
              <Icons icon="pin" size="20" weight="bold" color="primary" />
            </div>
            <div className="block lg:hidden">
              <Icons icon="pin" size="12" weight="bold" color="primary" />
            </div>
            <p className="text-caption-primary text-content-primary">{city}</p>
          </li>
        )}

        <li className="inline-flex items-center gap-1">
          <div className="hidden lg:block">
            <Icons icon="user" size="20" weight="bold" color="primary" />
          </div>
          <div className="block lg:hidden">
            <Icons icon="user" size="12" weight="bold" color="primary" />
          </div>
          <p className="text-caption-primary text-content-primary">{t('components.profile.public.member-since', { ns: 'common', createdAt: createdAt.locale('fr').format('DD MMM YYYY') })}</p>
        </li>

        {(isNice || isPunctual) && (
          <li className="inline-flex items-center gap-1">
            <div className="hidden lg:block">
              <Icons icon="award" size="20" weight="bold" color="primary" />
            </div>
            <div className="block lg:hidden">
              <Icons icon="award" size="12" weight="bold" color="primary" />
            </div>
            {isNice && <BadgeNice />}
            {isPunctual && <BadgePunctual />}
          </li>
        )}
      </ul>
    </section>
  );

  if (link) {
    return <Link href={new Routes.PublicProfileRoute(user.uuid)}>{info}</Link>;
  }

  return info;
};

export default ProfileInfo;
